/* Print settings panel (left column): document, printer, paper, appearance, options, copies. */
import { useId, type ReactNode } from "react";
import { Button, Field, Icon, Input, Seg, cx } from "../../../ui";
import { LOOKUP, useI18n } from "../../../i18n";
import type { Employee, Role } from "../../../data/types";
import { docDef, docsFor, LETTER_KINDS, PER_EMP, PERIODIC, SCOPED, type DocKey } from "../docs";
import { PAPER_LIST, type Dims, type MarginKey } from "../papers";
import { PRINTERS, printerFits, type ColorMode } from "../printers";
import { setCopies, setDoc, setOrient, setPaper, setPerSheet, setPrinter, type HeadKind, type Lang3, type PrState, type SizeKind, type WmKind } from "../state";
import { periodLabel } from "../../../lib/format";

export interface ControlsProps {
  s: PrState;
  set: (fn: (s: PrState) => PrState) => void;
  d: Dims;
  role: Role | null;
  emps: Employee[];
  periods: [string, boolean][];
  offboardingIds: number[];
}

function Sec({ title, children, testId }: { title: ReactNode; children: ReactNode; testId?: string }) {
  return <div className="prs-sec" data-testid={testId}><div className="prs-h">{title}</div>{children}</div>;
}

function LabeledSelect({ label, value, onChange, children, testId, hint }: { label: ReactNode; value: string | number; onChange: (v: string) => void; children: ReactNode; testId?: string; hint?: ReactNode }) {
  const id = useId();
  return <Field label={label} htmlFor={id} hint={hint}><select id={id} className="sel" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>{children}</select></Field>;
}

export default function Controls({ s, set, d, role, emps, periods, offboardingIds }: ControlsProps) {
  const { L, t, lang } = useI18n();
  const doc = docDef(s.doc);
  const isPage = d.fam === "page" || d.fam === "labelsheet";
  const isEmp = role === "employee";
  const up = <K extends keyof PrState>(k: K, v: PrState[K]) => set((x) => ({ ...x, [k]: v }));

  const paperOpts = PAPER_LIST.filter((p) => doc.fams.includes(p.fam));
  const groups = new Map<string, typeof paperOpts>();
  for (const p of paperOpts) groups.set(p.group, [...(groups.get(p.group) || []), p]);

  const showEmp = PER_EMP.includes(s.doc) && !isEmp && (!SCOPED.includes(s.doc) || s.scope === "one");
  const dimsTxt = d.w.toFixed(1) + " × " + (d.h ? d.h.toFixed(1) : L("auto", "تلقائي")) + " mm";
  const deptOpts: [string, string][] = [["", t("All departments")], ...Object.keys(LOOKUP.DEPT).map((k) => [k, L(LOOKUP.DEPT[k][0], LOOKUP.DEPT[k][1])] as [string, string])];

  return (
    <aside className="card prs" data-testid="print-controls" aria-label={L("Print settings", "إعدادات الطباعة")}>
      <Sec title={L("Document", "المستند")}>
        <LabeledSelect label={L("Document", "المستند")} value={s.doc} onChange={(v) => set((x) => setDoc(x, v as DocKey))} testId="pr-doc">
          {docsFor(role).map((x) => <option key={x.key} value={x.key}>{L(x.en, x.ar)}</option>)}
        </LabeledSelect>
        <div className="stack" style={{ gap: 10, marginTop: 10 }}>
          {SCOPED.includes(s.doc) && !isEmp && (
            <Field label={L("Print for", "الطباعة لـ")}>
              <Seg label={L("Print for", "الطباعة لـ")} items={[["one", L("One employee", "موظف واحد")], ["all", L("All employees", "كل الموظفين")]]} value={s.scope} onChange={(v) => up("scope", v)} />
            </Field>
          )}
          {showEmp && (
            <LabeledSelect label={t("Employee")} value={s.emp} onChange={(v) => up("emp", Number(v))} testId="pr-emp">
              {emps.map((e) => <option key={e.id} value={e.id}>{(lang === "ar" ? e.nameAr : e.nameEn) + " · " + e.code + (s.doc === "settlement" && offboardingIds.includes(e.id) ? " · " + L("leaving", "مغادر") : "")}</option>)}
            </LabeledSelect>
          )}
          {PERIODIC.includes(s.doc) && (
            <LabeledSelect label={t("Month")} value={s.period} onChange={(v) => up("period", v)} testId="pr-period">
              {periods.map(([p, draft]) => <option key={p} value={p}>{periodLabel(p, lang) + (draft ? " " + L("(draft)", "(مسودة)") : "")}</option>)}
            </LabeledSelect>
          )}
          {(s.doc === "register" || s.doc === "directory" || s.doc === "attendance" || ((s.doc === "payslip" || s.doc === "cheque" || s.doc === "label") && s.scope === "all")) && !isEmp && (
            <LabeledSelect label={t("Department")} value={s.dept} onChange={(v) => up("dept", v)} testId="pr-dept">
              {deptOpts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </LabeledSelect>
          )}
          {s.doc === "attendance" && (
            <MonthField label={t("Month")} value={(s.date || "").slice(0, 7)} onChange={(v) => up("date", v)} />
          )}
          {s.doc === "settlement" && (
            <DateField label={L("Last working day (quote)", "آخر يوم عمل (للتقدير)")} value={s.date.length === 10 ? s.date : ""} onChange={(v) => up("date", v)} hint={L("Used only when the employee has no offboarding record", "يُستخدم فقط إذا لم يكن للموظف طلب إنهاء خدمة")} />
          )}
          {s.doc === "letter" && (
            <LabeledSelect label={L("Letter type", "نوع الخطاب")} value={LETTER_KINDS.some((k) => k[0] === s.letter) ? s.letter : LETTER_KINDS[0][0]} onChange={(v) => up("letter", v)} testId="pr-letter">
              {LETTER_KINDS.map((k) => <option key={k[0]} value={k[0]}>{L(k[1], k[2])}</option>)}
            </LabeledSelect>
          )}
          {s.doc === "payslip" && isPage && (
            <Field label={L("Payslips per sheet", "قسائم في كل ورقة")} hint={s.perSheet === 2 && s.orient !== "portrait" ? L("Two per sheet needs portrait", "قسيمتان تتطلب الوضع الطولي") : L("Two per sheet halves paper use on A4/Letter", "قسيمتان في الورقة توفر نصف الورق")}>
              <Seg label={L("Payslips per sheet", "قسائم في كل ورقة")} items={[["1", "1"], ["2", L("2 (cut line)", "2 (خط قص)")]]} value={String(s.perSheet) as "1" | "2"} onChange={(v) => set((x) => setPerSheet(x, v === "2" ? 2 : 1))} />
            </Field>
          )}
        </div>
      </Sec>

      <Sec title={L("Printer", "الطابعة")} testId="pr-printers">
        <div className="prns">
          {PRINTERS.map((x) => {
            const ok = printerFits(x, doc.fams);
            return <button key={x.key} type="button" className={cx("prn", s.printer === x.key && "on")} disabled={!ok} aria-pressed={s.printer === x.key} onClick={() => set((st) => setPrinter(st, x.key))} data-testid={"prn-" + x.key}><Icon name={x.icon} /><span>{L(x.en, x.ar)}</span></button>;
          })}
        </div>
      </Sec>

      <Sec title={L("Paper", "الورق")}>
        <LabeledSelect label={L("Paper size", "مقاس الورق")} value={s.paper} onChange={(v) => set((x) => setPaper(x, v))} testId="pr-paper">
          {[...groups.entries()].map(([g, ps]) => (
            <optgroup key={g} label={g}>{ps.map((p) => <option key={p.key} value={p.key}>{p.name + (p.w && p.h && p.key !== "L7050" ? " · " + p.w + "×" + p.h + " mm" : "")}</option>)}</optgroup>
          ))}
        </LabeledSelect>
        {s.paper === "CUSTOM" && (
          <div className="form2" style={{ marginTop: 10 }}>
            <NumField label={L("Width", "العرض")} value={s.cw} onChange={(v) => up("cw", v)} testId="pr-cw" />
            <NumField label={L("Height", "الارتفاع")} value={s.ch} onChange={(v) => up("ch", v)} testId="pr-ch" />
          </div>
        )}
        <div className="row mt" style={{ gap: 10 }}>
          {d.fam !== "roll"
            ? <Seg label={L("Orientation", "الاتجاه")} items={[["portrait", L("Portrait", "طولي")], ["landscape", L("Landscape", "عرضي")]]} value={s.orient} onChange={(v) => set((x) => setOrient(x, v))} />
            : <span className="chip">{L("Continuous roll · auto length", "لفة مستمرة · طول تلقائي")}</span>}
          <span className="xs muted num" data-testid="pr-dims">{dimsTxt}</span>
        </div>
        {isPage && (
          <div className="mt">
            <Field label={L("Margins", "الهوامش")} hint={L("Millimetres. Inkjet and laser printers need at least 3–5 mm.", "بالمليمتر. تحتاج طابعات الحبر والليزر 3–5 مم على الأقل.")}>
              <Seg label={L("Margins", "الهوامش")} items={[["none", L("None", "بدون")], ["narrow", "6"], ["normal", "12"], ["wide", "20"]]} value={s.margin} onChange={(v) => up("margin", v as MarginKey)} />
            </Field>
          </div>
        )}
      </Sec>

      <Sec title={L("Appearance", "المظهر")}>
        <div className="stack" style={{ gap: 10 }}>
          <Field label={L("Colour", "الألوان")} hint={s.color === "draft" ? L("Monospace, no fills — best for dot-matrix and fax", "خط ثابت دون تعبئة — الأنسب للطابعات النقطية والفاكس") : undefined}>
            <Seg label={L("Colour", "الألوان")} items={[["color", L("Colour", "ملون")], ["gray", L("Grayscale", "رمادي")], ["draft", L("B&W draft", "أبيض وأسود")]]} value={s.color} onChange={(v) => up("color", v as ColorMode)} />
          </Field>
          <Field label={t("Language")}>
            <Seg label={t("Language")} items={[["bi", L("Bilingual", "ثنائي")], ["en", "English"], ["ar", "العربية"]]} value={s.lang} onChange={(v) => up("lang", v as Lang3)} />
          </Field>
          <Field label={L("Text size", "حجم النص")}>
            <Seg label={L("Text size", "حجم النص")} items={[["small", L("Small", "صغير")], ["normal", L("Normal", "عادي")], ["large", L("Large", "كبير")]]} value={s.size} onChange={(v) => up("size", v as SizeKind)} />
          </Field>
          {isPage && (
            <>
              <LabeledSelect label={L("Letterhead", "الترويسة")} value={s.head} onChange={(v) => up("head", v as HeadKind)} testId="pr-head">
                <option value="full">{L("Full letterhead", "ترويسة كاملة")}</option><option value="simple">{L("Simple header", "ترويسة مختصرة")}</option>
                <option value="pre">{L("Pre-printed stationery (leave space)", "ورق مطبوع مسبقًا (ترك مساحة)")}</option><option value="none">{L("None", "بدون")}</option>
              </LabeledSelect>
              <LabeledSelect label={L("Watermark", "العلامة المائية")} value={s.wm} onChange={(v) => up("wm", v as WmKind)} testId="pr-wm">
                <option value="none">{L("None", "بدون")}</option><option value="copy">{L("COPY", "نسخة")}</option><option value="conf">{L("CONFIDENTIAL", "سري")}</option>
                <option value="draft">{L("DRAFT", "مسودة")}</option><option value="orig">{L("ORIGINAL", "أصل")}</option>
              </LabeledSelect>
            </>
          )}
        </div>
      </Sec>

      <Sec title={L("Options", "الخيارات")}>
        <div className="stack" style={{ gap: 6 }}>
          {([["qr", L("Verification QR code", "رمز التحقق QR")], ["sign", L("Signature lines", "خانات التوقيع")], ["stamp", L("Company stamp area", "مكان ختم المنشأة")], ["pageNo", L("Page numbers & print footer", "أرقام الصفحات وتذييل الطباعة")], ["duplex", L("Two-sided (duplex)", "طباعة على الوجهين")]] as ["qr" | "sign" | "stamp" | "pageNo" | "duplex", string][]).map(([k, l]) => (
            <label className="switch" key={k}><input type="checkbox" checked={s[k]} onChange={(e) => up(k, e.target.checked)} data-testid={"pr-" + k} />{l}</label>
          ))}
          <div className="mt">
            <Field label={L("Copies", "عدد النسخ")} hint={s.copies > 1 ? L("First set is marked ORIGINAL, the rest COPY", "المجموعة الأولى أصل والبقية نسخ") : undefined}>
              <div className="row nw">
                <Button variant="sm" icon="minus" aria-label={L("Fewer copies", "نسخ أقل")} disabled={s.copies <= 1} onClick={() => set((x) => setCopies(x, -1))} data-testid="pr-copies-minus" />
                <span className="num" style={{ minWidth: 28, textAlign: "center", fontWeight: 600 }} data-testid="pr-copies">{s.copies}</span>
                <Button variant="sm" icon="plus" aria-label={L("More copies", "نسخ أكثر")} disabled={s.copies >= 10} onClick={() => set((x) => setCopies(x, 1))} data-testid="pr-copies-plus" />
              </div>
            </Field>
          </div>
        </div>
      </Sec>
    </aside>
  );
}

function NumField({ label, value, onChange, testId }: { label: string; value: number; onChange: (v: number) => void; testId: string }) {
  const id = useId();
  return <Field label={label} htmlFor={id}><div className="affix"><Input id={id} type="number" className="num" value={value} min={20} max={1200} onChange={(e) => onChange(Number(e.target.value))} data-testid={testId} /><span className="end">mm</span></div></Field>;
}
function MonthField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const id = useId();
  return <Field label={label} htmlFor={id}><Input id={id} type="month" value={value} onChange={(e) => onChange(e.target.value)} data-testid="pr-month" /></Field>;
}
function DateField({ label, value, onChange, hint }: { label: string; value: string; onChange: (v: string) => void; hint?: ReactNode }) {
  const id = useId();
  return <Field label={label} htmlFor={id} hint={hint}><Input id={id} type="date" value={value} onChange={(e) => onChange(e.target.value)} data-testid="pr-date" /></Field>;
}

